import React from 'react';
import { cn } from '@/lib/cn';
import { Selection, BottomSheet } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import { FilterActionBar } from './FilterActionBar';
import { MoveInDateSelection, getMoveInDateDisplayLabel, getUpcomingMonths } from '@repo/utils';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface ExpectedMoveInDateFilterProps {
  variant?: 'desktop' | 'mobile';
  className?: string;
  requireConfirmation?: boolean;
  showLabel?: boolean;
  // Mobile-specific props
  open?: boolean;
  onClose?: () => void;
}

type FilterOption = {
  key: MoveInDateSelection;
  label: string;
};

// Shared hook for expected move-in date filter logic - exported for reuse
export function useExpectedMoveInDateFilter() {
  const analytics = useAnalytics();
  const expectedMoveInDate = useStore(s => s.listingsFilters.expectedMoveInDate);
  const toggleExpectedMoveInSelectionRaw = useStore(
    s => s.listingsFilters.toggleExpectedMoveInSelection,
  );
  const resetExpectedMoveInDate = useStore(s => s.listingsFilters.resetExpectedMoveInDate);

  const selections = expectedMoveInDate.selections as MoveInDateSelection[];

  const toggleExpectedMoveInSelection = (key: MoveInDateSelection) => {
    analytics.track('listings_future_move_in_date', { val: key });
    toggleExpectedMoveInSelectionRaw(key);
  };

  const formatExpectedMoveInDateDisplay = () => {
    if (selections.length === 0) {
      return undefined; // Show placeholder
    }
    return getMoveInDateDisplayLabel(selections);
  };

  // Generate filter options dynamically
  const filterOptions: FilterOption[] = React.useMemo(() => {
    const upcomingMonths = getUpcomingMonths();
    const monthOptions = upcomingMonths.map(month => ({
      key: month.key as MoveInDateSelection,
      label: month.label,
    }));

    return [{ key: 'asap' as MoveInDateSelection, label: 'As soon as possible' }, ...monthOptions];
  }, []);

  return {
    selections,
    toggleExpectedMoveInSelection,
    resetExpectedMoveInDate,
    formatExpectedMoveInDateDisplay,
    filterOptions,
  };
}

export function ExpectedMoveInDateFilter({
  variant = 'desktop',
  showLabel = true,
  className,
  requireConfirmation = false,
  open,
  onClose,
}: ExpectedMoveInDateFilterProps) {
  const { selections, toggleExpectedMoveInSelection, resetExpectedMoveInDate, filterOptions } =
    useExpectedMoveInDateFilter();

  const moveInDateSelectionContent = (
    <div
      className={cn('flex w-full flex-wrap items-start gap-2')}
      role="group"
      aria-label="Expected move-in date"
    >
      {filterOptions.map(({ key, label }) => {
        const selected = selections.includes(key);
        return (
          <Selection
            key={key}
            selected={selected}
            onClick={() => toggleExpectedMoveInSelection(key)}
          >
            {label}
          </Selection>
        );
      })}
    </div>
  );

  if (variant === 'mobile') {
    return (
      <BottomSheet
        label="Expected move-in date"
        bodyClassName="py-8 px-6"
        open={open!}
        onClose={onClose!}
        leading={false}
        footer={
          requireConfirmation ? (
            <FilterActionBar
              scope="moveInDate"
              onApply={() => {
                onClose?.();
              }}
              onReset={() => {
                resetExpectedMoveInDate();
              }}
            />
          ) : null
        }
      >
        <div className="flex flex-col gap-1">
          {showLabel && (
            <span className={cn('text-body-2-high text-secondary mb-3')}>
              Expected move-in date
            </span>
          )}
          {moveInDateSelectionContent}
        </div>
      </BottomSheet>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2 bg-primary', className)}>
      {showLabel && (
        <span className={cn('text-body-2-high text-secondary')}>Expected move-in date</span>
      )}
      {moveInDateSelectionContent}
      {requireConfirmation && (
        <FilterActionBar
          scope="moveInDate"
          className="mt-4"
          onReset={() => {
            resetExpectedMoveInDate();
          }}
        />
      )}
    </div>
  );
}

export default ExpectedMoveInDateFilter;
